import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Box,
  Button,
  HStack,
  Skeleton,
  SkeletonText,
  VStack,
} from "@chakra-ui/react";
import { BreadcrumbItem, BreadcrumbLink } from "@chakra-ui/breadcrumb";
import { PageHeading } from "../../components/PageHeading/PageHeading";
import { PageContent } from "../../components/PageContent/PageContent";
import { BusinessBreadcrumbs } from "../../components/Breadcrumbs/BusinessBreadcrumbs";
import { ViewControl } from "../../components/ViewControl/ViewControl";
import { EditIcon } from "../../styles/icons";
import { useAdminApiClient } from "../../hooks/useApiClient";
import { useAdminApiRequest } from "../../hooks/useApiRequest";
import { useToast } from "../../hooks/useToast";
import { CherryHubAdminApi } from "../../api/adminApiModels";
import { GeoLocationDetailForm } from "../../forms/GeoLocationDetailForm";

export const GeoLocationDetail: React.FC = () => {
  const { businessId, geoLocationId } = useParams();
  const apiClient = useAdminApiClient();
  const { showSuccessToast, showErrorToast } = useToast();

  const { data: geoLocationDoc, isLoading } = useAdminApiRequest(
    (apiClient) =>
      businessId && geoLocationId
        ? apiClient.getGeoLocation(businessId, geoLocationId)
        : null,
    [businessId, geoLocationId]
  );

  const [geoLocation, setGeoLocation] =
    useState<CherryHubAdminApi.GeoLocation | null>(null);
  const [editMode, setEditMode] = useState(false);

  useEffect(() => {
    if (geoLocationDoc) setGeoLocation(geoLocationDoc);
  }, [geoLocationDoc]);

  const onSuccess = (updated: CherryHubAdminApi.GeoLocation) => {
    setGeoLocation(updated);
    setEditMode(false);
    showSuccessToast("Geo Location updated.");
  };

  const onFailure = (message?: string) => {
    showErrorToast(message ?? "Failed to update Geo Location.");
  };

  return (
    <>
      <PageHeading>
        <PageHeading.Title>Geo Location</PageHeading.Title>
      </PageHeading>
      <PageContent>
        <VStack alignItems="flex-start" width="100%" spacing={6}>
          <BusinessBreadcrumbs variant="pageHeading">
            <BreadcrumbItem>
              <BreadcrumbLink
                as={Link}
                to={`/admin/businesses/${businessId}/geo-fence-configuration`}
              >
                Geo-Fence Configuration
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbItem>
              <BreadcrumbLink
                as={Link}
                to={`/admin/businesses/${businessId}/geo-fence-configuration/geo-locations/${geoLocationId}`}
              >
                {geoLocation?.DisplayName ?? geoLocation?.GeoLocationId ?? "-"}
              </BreadcrumbLink>
            </BreadcrumbItem>
          </BusinessBreadcrumbs>

          {isLoading && (
            <Box w="100%">
              <Skeleton w="320px" height="32px" mb={4} />
              <SkeletonText noOfLines={8} spacing="4" />
            </Box>
          )}

          {!isLoading && geoLocation && editMode && businessId && (
            <GeoLocationDetailForm
              apiClient={apiClient}
              businessId={businessId}
              geoLocation={geoLocation}
              onCancel={() => setEditMode(false)}
              onSuccess={onSuccess}
              onFailure={onFailure}
            />
          )}

          {!isLoading && geoLocation && !editMode && (
            <>
              <HStack w="100%" justifyContent="flex-end">
                <Button
                  leftIcon={<EditIcon />}
                  colorScheme="cherryButton"
                  onClick={() => setEditMode(true)}
                >
                  Edit
                </Button>
              </HStack>

              <VStack w="100%" alignItems="flex-start" spacing={2}>
                <ViewControl
                  label="Display Name"
                  value={geoLocation.DisplayName ?? "-"}
                />
                <ViewControl
                  label="Geo Location Id"
                  value={geoLocation.GeoLocationId}
                />
                <ViewControl
                  label="Default Location Message"
                  value={geoLocation.RelevantText}
                />
                <ViewControl label="Latitude" value={geoLocation.Latitude} />
                <ViewControl label="Longitude" value={geoLocation.Longitude} />
                <ViewControl
                  label="Altitude"
                  value={geoLocation.Altitude ?? "-"}
                />
                <ViewControl
                  label="Address"
                  value={geoLocation.Address ?? "-"}
                />
              </VStack>
            </>
          )}
        </VStack>
      </PageContent>
    </>
  );
};
